import React from 'react';
import { Radio, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { useRadio } from '../context/RadioContext';
import { ShareButton } from './ShareButton';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_SRC = `${API_URL}/api/uploads/files/mindesign_radio_hero.png`;

/**
 * MINDESIGN RADIO — section view. Audio playback is controlled via the global
 * RadioContext so the same broadcast continues in the floating mini-player.
 */
export const MindesignRadio = () => {
  const {
    current,
    playing,
    muted,
    loading,
    error,
    queue,
    history,
    togglePlay,
    next,
    prev,
    toggleMute,
  } = useRadio();

  return (
    <section
      id="radio"
      className="relative py-24 px-6 overflow-hidden"
      data-testid="mindesign-radio-section"
    >
      <div className="absolute inset-0 bg-gunmetal-950"></div>
      <div
        className="absolute inset-0 opacity-30 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_SRC})` }}
      ></div>
      <div className="absolute inset-0 bg-gradient-to-b from-gunmetal-950 via-gunmetal-950/80 to-gunmetal-950"></div>

      <div className="relative max-w-6xl mx-auto">
        <div className="flex items-center justify-center gap-3 mb-4">
          <span className="h-px w-10 bg-amber-400/50" />
          <div
            className={`flex items-center gap-2 text-[10px] tracking-[0.4em] uppercase ${
              playing ? 'text-accent-red' : 'text-amber-400/80'
            }`}
          >
            <span
              className={`h-2 w-2 rounded-full ${
                playing ? 'bg-accent-red animate-pulse' : 'bg-amber-400/60'
              }`}
            />
            {playing ? 'ON AIR · LIVE' : 'STANDBY · 24/7 BROADCAST'}
          </div>
          <span className="h-px w-10 bg-amber-400/50" />
        </div>

        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="relative order-2 md:order-1">
            <div className="relative rounded-lg overflow-hidden border border-amber-400/30 shadow-2xl shadow-black/60">
              <img
                src={HERO_SRC}
                alt="MINDESIGN RADIO"
                className="w-full h-auto object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-gunmetal-950/60 to-transparent pointer-events-none" />
            </div>
          </div>

          <div className="order-1 md:order-2">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-steel-100 mb-4">
              MINDESIGN <span className="text-accent-red">RADIO</span>
            </h2>
            <p className="text-steel-400 text-base leading-relaxed mb-2">
              <em className="text-amber-400/90 not-italic">
                Fusing the Human Heart with AI Art.
              </em>
            </p>
            <p className="text-steel-400 text-base leading-relaxed mb-8">
              The 24/7 broadcast of the MINDESIGN ecosystem — THE REDACTED tracks,
              Garage Band rawness, score beds from the films and comics.
              Press ON AIR and let it run — the player follows you across the site.
            </p>

            <div
              className="bg-gunmetal-900/80 backdrop-blur-sm border border-accent-red/30 rounded-lg p-5 shadow-xl"
              data-testid="radio-player"
            >
              {loading ? (
                <div className="flex items-center gap-3 text-steel-400 py-3">
                  <Loader2 className="animate-spin" size={18} />
                  Tuning the dial…
                </div>
              ) : error ? (
                <div className="text-accent-red text-sm" data-testid="radio-error">
                  {error}
                </div>
              ) : current ? (
                <>
                  <div className="mb-4">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400/80 mb-1">
                      Now Broadcasting
                    </div>
                    <div className="text-xl font-bold text-steel-100 truncate" data-testid="radio-now-title">
                      {current.title}
                    </div>
                    <div className="text-sm text-steel-400 truncate">
                      {current.artist} · {current.genre || 'MINDESIGN'} · {current.duration}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={prev}
                      className="bg-gunmetal-800 hover:bg-gunmetal-700 text-steel-200 px-3"
                      data-testid="radio-prev-btn"
                      aria-label="Previous track"
                    >
                      <SkipBack size={18} />
                    </Button>
                    <Button
                      type="button"
                      onClick={togglePlay}
                      className="flex-1 bg-accent-red hover:bg-accent-red-dark text-white font-black tracking-widest uppercase py-5"
                      data-testid="radio-play-btn"
                    >
                      {playing ? (
                        <>
                          <Pause className="mr-2" size={18} /> On Air — Pause
                        </>
                      ) : (
                        <>
                          <Play className="mr-2" size={18} /> Tune In
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      onClick={next}
                      className="bg-gunmetal-800 hover:bg-gunmetal-700 text-steel-200 px-3"
                      data-testid="radio-next-btn"
                      aria-label="Next track"
                    >
                      <SkipForward size={18} />
                    </Button>
                    <Button
                      type="button"
                      onClick={toggleMute}
                      className="bg-gunmetal-800 hover:bg-gunmetal-700 text-steel-200 px-3"
                      data-testid="radio-mute-btn"
                      aria-label="Mute"
                    >
                      {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                    </Button>
                  </div>

                  <div className="mt-3 text-xs text-steel-500 flex items-center justify-between">
                    <span data-testid="radio-queue-count">
                      Shuffled queue: {queue.length} tracks
                    </span>
                    <span className="flex items-center gap-1.5 text-amber-400/70">
                      <Radio size={12} /> MDE · 24/7
                    </span>
                  </div>

                  {history.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-steel-800/60" data-testid="radio-history">
                      <div className="text-[10px] tracking-[0.3em] uppercase text-amber-400/70 mb-2">
                        Just Played
                      </div>
                      <ul className="space-y-1">
                        {history.slice(0, 3).map((t, i) => (
                          <li
                            key={`${t.id || t.title}-${i}`}
                            className="text-xs text-steel-400 truncate flex items-center gap-2"
                          >
                            <span className="text-steel-600 w-4 text-right">{i + 1}.</span>
                            <span className="text-steel-300 font-medium">{t.title}</span>
                            <span className="text-steel-600">·</span>
                            <span className="text-steel-500">{t.artist}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-steel-800/60 flex justify-center">
                    <ShareButton
                      sectionId="radio"
                      sectionTitle="MINDESIGN Radio"
                      description="24/7 broadcast — 40+ original tracks from THE REDACTED, JFRJR, and the MINDESIGN ENTERPRISES vault. Fusing the Human Heart with AI Art."
                    />
                  </div>
                </>
              ) : (
                <div className="text-steel-400">No broadcast available right now.</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MindesignRadio;
